import type { LocalHorizontal } from "../world/coordinates.ts";

export const MAP_MARKER_STORAGE_KEY = "vilnius.map-marker.v1";

export interface PersistedMapMarker {
  readonly schemaVersion: 1;
  readonly position: LocalHorizontal;
}

/**
 * Provisional full-map interaction values. They shape presentation only and
 * should receive a hands-on user tuning pass before becoming final.
 */
export const FULL_MAP_CONFIG = Object.freeze({
  initialRadiusM: 420,
  minimumRadiusM: 42,
  maximumRadiusM: 20_000,
  detailedTileMaximumRadiusM: 1_800,
  zoomStep: 1.28,
  wheelZoomSensitivity: 0.00125,
  keyboardPanFraction: 0.16,
  dragThresholdCssPx: 5,
  maxDevicePixelRatio: 2,
  tilePrefetchMarginM: 256,
  maxCachedTiles: 96,
  maxConcurrentLoads: 2,
});

export type FullMapPlaceImportance = 1 | 2 | 3;

const PRIMARY_MAP_PLACES = new Map<string, string>([
  ["vilnius-cathedral", "Cathedral"],
  ["gediminas-castle", "Gediminas Tower"],
  ["vilnius-tv-tower", "TV Tower"],
  ["vilnius-airport-historic-terminal", "Vilnius Airport"],
  ["verkiai-palace", "Verkiai Palace"],
  ["belmontas-mill-complex", "Belmontas"],
  ["akropolis-vilnius", "Akropolis"],
  ["vilnius-railway-station", "Railway Station"],
  ["white-bridge", "White Bridge"],
]);

const SECONDARY_MAP_PLACES = new Map<string, string>([
  ["vingis-park-stage", "Vingis Park"],
  ["vilnius-town-hall", "Town Hall"],
  ["gate-of-dawn", "Gate of Dawn"],
  ["three-crosses", "Three Crosses"],
  ["lukiskes-prison", "Lukiškės Prison"],
  ["palace-grand-dukes", "Palace of the Grand Dukes"],
  ["hales-market", "Halės Market"],
  ["mo-museum", "MO Museum"],
  ["national-gallery-of-art", "National Gallery"],
  ["vilnius-bus-station", "Bus Station"],
  ["seimas-palace", "Seimas"],
  ["church-st-peter-and-st-paul", "St Peter and St Paul"],
  ["rasos-cemetery", "Rasos Cemetery"],
  ["puckoriai-viewpoint", "Pūčkoriai Viewpoint"],
  ["subacius-viewpoint", "Subačius Viewpoint"],
  ["paupys-market-quarter", "Paupys"],
]);

export function fullMapPlacePresentation(
  id: string,
  sourceLabel: string,
): Readonly<{ label: string; importance: FullMapPlaceImportance }> {
  const primaryLabel = PRIMARY_MAP_PLACES.get(id);
  if (primaryLabel !== undefined) {
    return { label: primaryLabel, importance: 1 };
  }
  const secondaryLabel = SECONDARY_MAP_PLACES.get(id);
  if (secondaryLabel !== undefined) {
    return { label: secondaryLabel, importance: 2 };
  }
  return { label: sourceLabel, importance: 3 };
}

function parseFinitePosition(value: unknown): LocalHorizontal | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  return typeof record.x === "number" && Number.isFinite(record.x) &&
      typeof record.z === "number" && Number.isFinite(record.z)
    ? { x: record.x, z: record.z }
    : null;
}

export function parseMapMarker(serialized: string): LocalHorizontal | null {
  try {
    const value = JSON.parse(serialized) as unknown;
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
      return null;
    }
    const record = value as Record<string, unknown>;
    return record.schemaVersion === 1
      ? parseFinitePosition(record.position)
      : null;
  } catch {
    return null;
  }
}

export function loadMapMarker(
  storage: Pick<Storage, "getItem">,
): LocalHorizontal | null {
  try {
    const serialized = storage.getItem(MAP_MARKER_STORAGE_KEY);
    return serialized === null ? null : parseMapMarker(serialized);
  } catch {
    return null;
  }
}

export function persistMapMarker(
  storage: Pick<Storage, "setItem" | "removeItem">,
  marker: Readonly<LocalHorizontal> | null,
): boolean {
  try {
    if (marker === null) {
      storage.removeItem(MAP_MARKER_STORAGE_KEY);
    } else {
      const persisted: PersistedMapMarker = {
        schemaVersion: 1,
        position: { x: marker.x, z: marker.z },
      };
      storage.setItem(MAP_MARKER_STORAGE_KEY, JSON.stringify(persisted));
    }
    return true;
  } catch {
    return false;
  }
}
